import { Loader } from '../Widgets';
import React, { useState, useEffect, useRef } from 'react';
import { useSelector } from "react-redux";
import { Session } from 'service';
import ArthaGateway from 'service/ArthaGateway';
import { RootState } from 'store/store';
import AsyncSelect from 'react-select/async';
import moment from 'moment';
import Swal from 'sweetalert2';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { faPhone } from '@fortawesome/free-solid-svg-icons';

export const Dealers = () => {
    const appdata = useSelector((state: RootState) => state.appdata);
    const [session, setSession] = useState<Session>();
    const [isLoading, setIsLoading] = useState<boolean>(true);
    const [dealerId, setDealerId] = useState<number>(0);
    const [dealer, setDealer] = useState<any>(null);
    const [dealerName, setDealerName] = useState<string>("");
    const [businessSummary, setBusinessSummary] = useState<any>(null);
    const [outstandingAnalysis, setOutstandingAnalysis] = useState<any>(null);
    const [isBusinessSummaryOpen, setBusinessSummaryOpen] = useState(false);
    const [isOutstandingAnalysisOpen, setOutstandingAnalysisOpen] = useState(false);
    const outstandingSlabs = [
        {
            min: 1,
            max: 30
        },
        {
            min: 30,
            max: 45
        },
        {
            min: 45,
            max: 60
        },
        {
            min: 60,
            max: 90
        },
        {
            min: 90,
            max: 10000
        }
    ];

    useEffect(() => {
        if (appdata.profile) {
            setSession(appdata.profile.profile);
        }
    }, [appdata]);

    const onDealerSelection = selectedDealer => {
        setDealerName(selectedDealer ? selectedDealer.label : "");
        setDealerId(selectedDealer ? selectedDealer.value.id : 0);
    };

    useEffect(() => {
        if (dealerId > 0) {
            loadDealerDetails(dealerId);
        }
        else {
            setDealer(null);
            setBusinessSummary(null);
            setOutstandingAnalysis(null);
            setBusinessSummaryOpen(false);
            setOutstandingAnalysisOpen(false);
            setIsLoading(false);
        }
    }, [dealerId]);

    const onSearchDealer = (inputValue: string) => {
        return new Promise<any[]>(async (resolve) => {
            const data = await ArthaGateway.getDealers(-1, -1, inputValue);
            var mappedData = data.entries.map(function (dealer) {
                return {
                    value: dealer,
                    label: dealer.name
                };
            });
            resolve(mappedData);
        });
    };

    const loadDealerDetails = async (id: number) => {
        setIsLoading(true);
        const data = await ArthaGateway.getDealerDetails(id);
        if (data) {
            setDealer(data);
        }
        setIsLoading(false);
    };

    const approveParty = async () => {
        if (!dealer) return;

        setIsLoading(true);
        const result = await Swal.fire({
            title: 'Confirm approve',
            text: `Are you sure you want to approve ${dealer.name}?`,
            icon: 'warning',
            showCancelButton: true,
            confirmButtonText: 'Approve',
            cancelButtonText: 'Cancel',
            focusCancel: true,
        });

        if (result.isConfirmed) {
            setIsLoading(true);
            try {
                const response = await ArthaGateway.approveParty(dealer.partyId);
                setIsLoading(false);
                if (response === 'Done') {
                    await Swal.fire({
                        title: 'Approved!',
                        text: `${dealer.name} has been approved.`,
                        icon: 'success',
                    });
                    loadDealerDetails(dealer.id);
                } else {
                    await Swal.fire({
                        title: 'Error!',
                        text: `Failed to approve ${dealer.name}.`,
                        icon: 'error',
                    });
                }
            } catch (error) {
                setIsLoading(false);
                await Swal.fire({
                    title: 'Error!',
                    text: `Failed to approve ${dealer.name}.`,
                    icon: 'error',
                });
            }
        }
    };

    const unblockParty = async () => {
        if (!dealer) return;
        setIsLoading(true);

        const result = await Swal.fire({
            title: 'Confirm unblock',
            text: `Are you sure you want to unblock ${dealer.name}?`,
            icon: 'warning',
            showCancelButton: true,
            confirmButtonText: 'Unblock',
            cancelButtonText: 'Cancel',
            focusCancel: true,
        });

        if (result.isConfirmed) {
            setIsLoading(true);
            try {
                const response = await ArthaGateway.unblockParty(dealer.partyId);
                setIsLoading(false);

                if (response === 'Done') {
                    await Swal.fire({
                        title: 'Unblocked!',
                        text: `${dealer.name} has been unblocked.`,
                        icon: 'success',
                    });
                    loadDealerDetails(dealer.id);
                } else {
                    await Swal.fire({
                        title: 'Error!',
                        text: `Failed to unblock ${dealer.name}.`,
                        icon: 'error',
                    });
                }
            } catch (error) {
                setIsLoading(false);
                await Swal.fire({
                    title: 'Error!',
                    text: `Failed to unblock ${dealer.name}.`,
                    icon: 'error',
                });
            }
        }
    };

    useEffect(() => {
        if (!dealer) return;

        if (businessSummary != null) {
            console.log("Business summary already loaded.");
            return;
        }
        console.log("Loading business summary data...");
        setIsLoading(true);
        let financialYearStart = moment().month(3).startOf('month'); // April 1st
        let year = moment().year();
        if (moment().isBefore(financialYearStart)) {
            year = financialYearStart.subtract(1, 'year').year();
        }
        if ((isBusinessSummaryOpen || isOutstandingAnalysisOpen)) {
            // Load business summary data
            ArthaGateway.getBusinessSummary(dealer.partyId, year).then(data => {
                setBusinessSummary(data);
                setIsLoading(false);
            });
        }
    }, [isBusinessSummaryOpen, isOutstandingAnalysisOpen]);

    useEffect(() => {
        if (!dealer) return;
        if (!businessSummary) return;
        if (!businessSummary.pendingInvoices) return;

        calculateSummaryReport();
    }, [businessSummary]);

    const calculateSummaryReport = function () {
        let analysiss = {
            totalBusiness: 0,
            totalPaid: 0,
            totalPending: 0,
            paidPercentage: 100,
            slabs: outstandingSlabs.map(() => 0) // initialize slabs array with zeros
        };

        businessSummary.pendingInvoices.forEach(invoice => {
            analysiss.totalBusiness += invoice.billAmount;
            analysiss.totalPaid += invoice.paidAmount;
            analysiss.totalPending += invoice.pendingAmount;

            // Find matching slab index once per invoice
            const slabIndex = outstandingSlabs.findIndex(slab =>
                invoice.dueDays >= slab.min && invoice.dueDays <= slab.max
            );

            if (slabIndex !== -1) {
                analysiss.slabs[slabIndex] += invoice.pendingAmount;
            }
        });

        // Calculate paid percentage once after loop
        if (analysiss.totalBusiness > 0) {
            analysiss.paidPercentage = Math.round((analysiss.totalPaid * 10000) / analysiss.totalBusiness) / 100;
        } else {
            analysiss.paidPercentage = 100;
        }
        setOutstandingAnalysis(analysiss);
    }

    const renderContactSegments = (str) => {
        // Split by spaces or commas, adjust delimiter as needed
        const segments = str.split(/\s+|,|;/).filter(Boolean);

        return segments.map((segment, index) => {
            const cleaned = segment.replace(/\D/g, ''); // Remove non-digit chars
            if (cleaned.length === 10) {
                // render as clickable tel link
                return (
                    <div className="mb-2" key={index} style={{ marginLeft: "8px" }}>
                        <a
                            key={index}
                            href={`tel:${cleaned}`}
                            style={{ textDecoration: "none", color: "#000" }}
                            className="fw-bold"
                        >
                            <FontAwesomeIcon icon={faPhone} style={{ width: "auto", float: "right" }} />
                            {segment}
                        </a>
                    </div>
                );
            } else {
                // render as span
                return (
                    <div className="mb-2" key={index} style={{ marginLeft: "8px" }}>
                        {segment}
                    </div>
                );
            }
        });
    };

    const getNetSales = () => {
        let netSales = 0;
        if (businessSummary?.sales != undefined) {
            netSales += businessSummary?.sales;
        }
        if (businessSummary?.return != undefined) {
            netSales -= businessSummary?.return;
        }
        if (businessSummary?.creditNote != undefined) {
            netSales -= businessSummary?.creditNote;
        }
        if (businessSummary?.journal != undefined) {
            netSales -= businessSummary?.journal;
        }
        return netSales;
    };

    const currencyFormat = (value) =>
        new Intl.NumberFormat('en-IN', {
            style: 'currency',
            currency: 'INR'
        }).format(value);

    return (
        <div className='page page-dealers'>
            <div className="container-fluid min-vh-100 p-2">
                <div className='row mb-3'>
                    <div className="col-12">
                        <AsyncSelect
                            cacheOptions
                            onChange={onDealerSelection}
                            loadOptions={onSearchDealer}
                            value={dealerId == 0 ? null : { value: dealer, label: dealerName }}
                            isClearable
                            classNamePrefix="react-select"
                            menuPortalTarget={document.body}
                            styles={{ menuPortal: base => ({ ...base, zIndex: 9999 }) }}
                            placeholder="Select a dealer"
                        />
                    </div>
                </div>
                {dealer && dealer?.name?.length > 0 && (
                    <>
                        <div className="card shadow-sm mb-3">
                            <div className="card-header bg-dark text-light">
                                <h5 className="mb-0">{dealer.name}</h5>
                            </div>
                            <div className="card-body p-0 p-md-3">
                                <ul className="list-group list-group-flush">
                                    <li className="list-group-item p-1">
                                        <div>Dealer Code:</div>
                                        <strong>{dealer.partyId}</strong>
                                    </li>
                                    <li className="list-group-item p-1">
                                        <div>Contact Person:</div>
                                        <strong>{dealer.contactPerson}</strong>
                                    </li>
                                    <li className="list-group-item p-1">
                                        <div>Mobile:</div>
                                        {renderContactSegments(dealer.mobileNumber)}
                                    </li>
                                    <li className="list-group-item p-1">
                                        <div>Dealer Type:</div>
                                        <strong>{dealer.dealerTypeName}</strong>
                                    </li>
                                    <li className="list-group-item p-1">
                                        <div>GSTIN:</div>
                                        <strong>{dealer.gstin}</strong>
                                    </li>
                                    <li className="list-group-item p-1">
                                        <div>Fertilizer License Number:</div>
                                        <strong>{dealer.licenseNumber ? dealer.licenseNumber : "N/A"}</strong>
                                    </li>
                                    {dealer.licenseNumber && (
                                        <li className="list-group-item p-1">
                                            <div>Fertilizer License Expiry:</div>
                                            <strong>{moment(dealer.licenseExpiry).format("DD-MM-YYYY")}</strong>
                                        </li>
                                    )}
                                    <li className="list-group-item p-1">
                                        <div>Pesticide License Number:</div>
                                        <strong>{dealer.licenseNumber1 ? dealer.licenseNumber1 : "N/A"}</strong>
                                    </li>
                                    {dealer.licenseNumber1 && (
                                        <li className="list-group-item p-1">
                                            <div>Pesticide License Expiry:</div>
                                            <strong>{moment(dealer.licenseExpiry1).format("DD-MM-YYYY")}</strong>
                                        </li>
                                    )}
                                    <li className="list-group-item">
                                        Cheque Number
                                        {dealer.chequeNumber.length > 0 &&
                                            <div className="fw-bold">{dealer.chequeNumber}</div>
                                        }
                                        {dealer.chequeNumber.length == 0 &&
                                            <>
                                                <br />
                                                <div className="badge bg-danger">No Cheque</div>
                                            </>
                                        }
                                    </li>
                                    <li className="list-group-item p-1">
                                        <div>Address:</div>
                                        <strong>
                                            {dealer.billingAddress1}<br />
                                            {dealer.billingAddress2 && (<>{dealer.billingAddress2}<br /></>)}
                                            {dealer.billingCity}, {dealer.billingState} - {dealer.billingPincode}
                                        </strong>
                                    </li>
                                    <li className="list-group-item p-1">
                                        <div>Active From:</div>
                                        <strong>
                                            {moment(dealer.createdAt).format("DD-MM-YYYY")}
                                            ({moment().diff(moment(dealer.createdAt), 'years')} years)
                                        </strong>
                                    </li>
                                    <li className="list-group-item p-1">
                                        <div>Is Credit Note Enabled?:</div>
                                        <strong>{dealer.enableCreditNote ? "Yes" : "No"}</strong>
                                    </li>
                                    {dealer.enableCreditNote && (
                                        <>
                                            <li className="list-group-item p-1">
                                                <div>Credit Limit:</div>
                                                <strong>{currencyFormat(dealer.creditLimit)}</strong>
                                            </li>
                                            <li className="list-group-item p-1">
                                                <div>Credit Days:</div>
                                                <strong>{dealer.maximumCreditDays} days</strong>
                                            </li>
                                        </>
                                    )}
                                    <li className="list-group-item p-1">
                                        <div>Is Coupons Enabled?:</div>
                                        <strong>{dealer.enableCoupons ? "Yes" : "No"}</strong>
                                    </li>
                                    <li className="list-group-item p-1">
                                        <div>Notes:</div>
                                        <div dangerouslySetInnerHTML={{ __html: dealer.dealerNotes }} />
                                    </li>
                                    <li className="list-group-item p-1">
                                        <div>Current Balance:</div>
                                        <strong className={dealer.totalBalance > 0 ? "text-danger" : "text-success"}>
                                            {currencyFormat(dealer.totalBalance)}
                                        </strong>
                                    </li>
                                    <li className="list-group-item p-1">
                                        <div>Sales Person:</div>
                                        <strong>{dealer.salesPerson.name}</strong>
                                    </li>
                                    {dealer.salesPerson.reportsToId > 0 && (
                                        <li className="list-group-item p-1">
                                            <div>Sales Person (Reports to):</div>
                                            <strong>{dealer.salesPerson.reportsTo}</strong>
                                        </li>
                                    )}
                                    <li className="list-group-item p-1">
                                        <div>Sales Person Mobile:</div>
                                        {renderContactSegments(dealer.salesPerson.mobileNumber)}
                                    </li>
                                    <li className='list-group-item p-1'>
                                        <div>Status:</div>
                                        <div className={`badge ${dealer.status == 'Approved' ? 'bg-success' : 'bg-danger'}`}>{dealer.status}</div>
                                    </li>
                                    {(dealer.status === 'Created' || dealer.status === 'OnHold') && (
                                        <li className='list-group-item p-1 text-center'>
                                            <div className="btn btn-danger w-100" title="Approve" onClick={approveParty}>
                                                Approve
                                            </div>
                                        </li>
                                    )}
                                    {(dealer.status === 'Blocked') && (
                                        <li className='list-group-item p-1 text-center'>
                                            <div className="btn btn-danger w-100" title="Unblock" onClick={unblockParty}>
                                                Unblock for today
                                            </div>
                                        </li>
                                    )}
                                </ul>
                            </div>
                        </div>

                        {/* Accordion for business summary & outstanding */}
                        <div className="accordion" id="customerAccordion">
                            <div className="accordion-item">
                                <h2 className="accordion-header" id="headingOne">
                                    <button
                                        className={`accordion-button ${!isBusinessSummaryOpen ? "collapsed" : ""} bg-dark text-light`}
                                        type="button"
                                        onClick={() => setBusinessSummaryOpen(!isBusinessSummaryOpen)}
                                    >
                                        Business Summary
                                    </button>
                                </h2>
                                {isBusinessSummaryOpen && (
                                    <>
                                        {isLoading && (
                                            <div className="accordion-body p-0">
                                                <Loader />
                                            </div>
                                        )}
                                        {!isLoading && (
                                            <div className="accordion-body p-0">
                                                <ul className="list-group p-0 m-0">
                                                    <li className="list-group-item">
                                                        <div>Opening Balance:</div>
                                                        <div className='fw-bold float-end'><h1>{currencyFormat(businessSummary?.openingBalance)}</h1></div>
                                                    </li>
                                                    <li className="list-group-item">
                                                        <div>Sales:</div>
                                                        <div className='fw-bold float-end'><h1>{currencyFormat(businessSummary?.sales)}</h1></div>
                                                    </li>
                                                    <li className="list-group-item">
                                                        <div>Return:</div>
                                                        <div className='fw-bold float-end'><h1>{currencyFormat(businessSummary?.return)}</h1></div>
                                                    </li>
                                                    {(businessSummary?.creditNote != undefined && businessSummary?.creditNote > 0) &&
                                                        <li className="list-group-item">
                                                            <div>Credit Note:</div>
                                                            <div className='fw-bold float-end'><h1>{currencyFormat(businessSummary?.creditNote)}</h1></div>
                                                        </li>
                                                    }
                                                    {(businessSummary?.journal != undefined && businessSummary?.journal > 0) &&
                                                        <li className="list-group-item">
                                                            <div>Other Adjustments:</div>
                                                            <div className='fw-bold float-end'><h1>{currencyFormat(businessSummary?.journal)}</h1></div>
                                                        </li>
                                                    }
                                                    <li className="list-group-item">
                                                        <div>Net Sales:</div>
                                                        <div className='fw-bold float-end'><h1>{currencyFormat(getNetSales())}</h1></div>
                                                    </li>
                                                    <li className="list-group-item">
                                                        <div>Receipt:</div>
                                                        <div className='fw-bold float-end'><h1>{currencyFormat(businessSummary?.receipt)}</h1></div>
                                                    </li>
                                                    <li className={(businessSummary?.closingBalance && businessSummary?.closingBalance > 0) ? "list-group-item bg-danger text-white fw-bold" : "list-group-item bg-success text-white fw-bold"}>
                                                        <div>Closing Balance:</div>
                                                        <div className='fw-bold float-end'><h1>{currencyFormat(businessSummary?.closingBalance)}</h1></div>
                                                    </li>
                                                </ul>
                                            </div>
                                        )}
                                    </>
                                )}
                            </div>

                            <div className="accordion-item mt-2">
                                <h2 className="accordion-header" id="headingTwo">
                                    <button
                                        className={`accordion-button ${!isOutstandingAnalysisOpen ? "collapsed" : ""} bg-dark text-light`}
                                        type="button"
                                        onClick={() => setOutstandingAnalysisOpen(!isOutstandingAnalysisOpen)}
                                    >
                                        Outstanding Analysis
                                    </button>
                                </h2>
                                {isOutstandingAnalysisOpen && (
                                    <>
                                        {isLoading && (
                                            <div className="accordion-body p-0">
                                                <Loader />
                                            </div>
                                        )}
                                        {!isLoading && (
                                            <div className="accordion-body">
                                                <ul className="list-group list-group-flush">
                                                    {outstandingSlabs.map((slab, index) => {
                                                        const amount = outstandingAnalysis.slabs[index] || 0;

                                                        // Label for slab range; e.g. "0-30 Days"
                                                        const label =
                                                            slab.max < 90
                                                                ? `${slab.min}-${slab.max} Days`
                                                                : slab.min >= 90
                                                                    ? `${slab.min}+ Days`
                                                                    : `${slab.min}-${slab.max} Days`;

                                                        // For slabs with min days 90 or above, use "text-danger fw-bold" classes
                                                        const isCritical = slab.min >= 90;

                                                        return (
                                                            <li
                                                                key={index}
                                                                className={`list-group-item p-1${isCritical ? ' text-danger fw-bold' : ''}`}
                                                            >
                                                                <div>{label}</div>
                                                                <div className='fw-bold float-end'><h1>{currencyFormat(amount)}</h1></div>
                                                            </li>
                                                        );
                                                    })}
                                                </ul>
                                            </div>
                                        )}
                                    </>
                                )}
                            </div>
                        </div>
                    </>
                )}
            </div>
        </div >
    );
};